import React from 'react';
import { CellContext } from '@tanstack/react-table';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions';

import { formatDate } from '@utils/kpi-utils/date';

import { formatTemplateWithTime, timezoneUTC } from '@constants/common';

interface IGetColumns {
	setEditableTaskId: React.Dispatch<React.SetStateAction<string | null>>;
	setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
	openDeleteConfirm: () => void;
	setSingleIdForRemove: React.Dispatch<React.SetStateAction<string | null>>;
	isProjectActive: boolean;
	setIsViewMode: React.Dispatch<React.SetStateAction<boolean>>;
	isPortalAdmin: boolean;
}

export const getColumns = ({
	setEditableTaskId,
	setIsEditMode,
	openDeleteConfirm,
	setSingleIdForRemove,
	isProjectActive,
	setIsViewMode,
	isPortalAdmin,
}: IGetColumns) => ([
	{
		header: 'Task ID',
		accessorKey: 'taskId',
		cell: ({ getValue }: CellContext<any, any>) => {
			const taskId = getValue();

			return <AdaptiveTableCell text={taskId} />;
		},
	},
	{
		header: 'Title',
		accessorKey: 'title',
		cell: ({ getValue }: CellContext<any, any>) => {
			const title = getValue();

			return <AdaptiveTableCell text={title} />;
		},
	},
	{
		header: 'Status',
		accessorKey: 'status',
		cell: ({ getValue }: CellContext<any, any>) => {
			const status = getValue();

			return <AdaptiveTableCell text={status?.title} />;
		},
	},
	{
		header: 'Assignees',
		accessorKey: 'assignees',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();
			const assignees = value?.map(({ fullName }: { fullName: string }) => fullName).join(', ');

			return <AdaptiveTableCell text={assignees} />;
		},
	},
	{
		header: 'Priority',
		accessorKey: 'priority',
		cell: ({ getValue }: CellContext<any, any>) => {
			const priority = getValue();

			return <AdaptiveTableCell text={priority?.title} />;
		},
	},
	{
		header: 'Milestone',
		accessorKey: 'milestoneName',
		cell: ({ getValue }: CellContext<any, any>) => {
			const milestone = getValue();

			return <AdaptiveTableCell text={milestone} />;
		},
	},
	{
		header: 'Start date',
		accessorKey: 'startDate',
		cell: ({ getValue }: CellContext<any, any>) => {
			const startDate = getValue();

			return <AdaptiveTableCell text={startDate ? startDate : '-'} />;
		},
	},
	{
		header: 'End date',
		accessorKey: 'endDate',
		cell: ({ getValue }: CellContext<any, any>) => {
			const endDate = getValue();

			return <AdaptiveTableCell text={endDate ? endDate : '-'} />;
		},
	},
	{
		header: 'Created',
		accessorKey: 'createdAt',
		cell: ({ getValue }: CellContext<any, any>) => {
			const createdAt = getValue();
			const date = createdAt ? formatDate(utcToZonedTime(createdAt, timezoneUTC), formatTemplateWithTime) : '';

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Updated',
		accessorKey: 'updatedAt',
		cell: ({ getValue }: CellContext<any, any>) => {
			const updatedAt = getValue();
			const date = updatedAt ? formatDate(utcToZonedTime(updatedAt, timezoneUTC), formatTemplateWithTime) : '';

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Actions',
		accessorKey: 'actions',
		enableSorting: false,
		cell: ({ row }: CellContext<any, any>) => {
			const handleEdit = () => {
				setEditableTaskId(row?.original.id);
				setIsEditMode(true);
			};

			const handleView = () => {
				setEditableTaskId(row?.original.id);
				setIsViewMode(true);
			};

			const handleDelete = () => {
				setSingleIdForRemove(row?.original?.id);
				openDeleteConfirm();
			};

			return (
				<Actions
					deleteTooltip='Delete task'
					editHandler={handleEdit}
					editTooltip='Edit task'
					handleDelete={handleDelete}
					isDelete={isProjectActive && isPortalAdmin}
					isEdit={isProjectActive}
					isView={!isProjectActive}
					viewHandler={handleView}
					viewTooltip='View task'
				/>
			);
		},
	},
]);
